import { DocumentationPageType, SubPageTypes } from './types';
import Stack from '@mui/material/Stack';
import { Typography, Button, IconButton } from '@sweep-io/sweep-design';
import Box from '@mui/material/Box';
import {
  selectActiveDocumentationPageTab,
  setActivePageTab,
  selectDocumentationPageType,
  setDocumentationPageType,
} from '../../reducers/documentation/documentationPageReducer';
import { useDispatch, useSelector } from 'react-redux';
import { ConfigurationCanvasPanelHeader } from '../pages/configuration-canvas-panel';
import { useExpandedMode } from '../pages/configuration-canvas/panels-reserve-space/useExpandedMode';
import { Breadcrumb, Breadcrumbs } from './selected-object/Breadcrumbs';
import { useCallback, useMemo, useState } from 'react';
import {
  selectSingleObject,
  selectSingleObjectActiveTab,
} from '../../reducers/documentation/documentationReducer';
import { DocumentationTabTypes } from '../../types/enums/DocumentationTabTypes';
import { useObjectListInteractions } from './object-list/useObjectListInteractions';
import { getMetadataLabel } from './tabs/getMetadataLabel';
import { Search } from '@sweep-io/sweep-design/dist/icons/Search';
import { useObjectListUniversalSearch } from './object-list/useObjectListUniversalSearch';
import { useFeatureToggle } from '../../feature-flags/useFeatureToggle';
import { DocumentationLightbox } from './universal-search/lightbox/DocumentationLightbox';

const pageTypeToName: { [key in SubPageTypes]: string } = {
  [SubPageTypes.objects]: 'Objects',
  [SubPageTypes.tags]: 'Tags',
  [SubPageTypes.users]: 'Sweep Users',
};

interface DocumentationPageHeaderProps {
  onCloseDialog?: () => void;
  withExpandedModeButton: boolean;
  renderBreadcrumbs?: boolean;
  crmOrgId: string;
}

export const DocumentationPageHeader = ({
  onCloseDialog,
  withExpandedModeButton,
  renderBreadcrumbs,
  crmOrgId,
}: DocumentationPageHeaderProps) => {
  const [isLightboxOpened, setIsLightboxOpened] = useState(false);
  const { universalSearchRedesign } = useFeatureToggle();

  const { isExpandedMode, isCanvasUnited } = useExpandedMode();
  const dispatch = useDispatch();
  const documentationPageType = useSelector(selectDocumentationPageType);
  const activePageTab = useSelector(selectActiveDocumentationPageTab);
  const objectTypeName = useSelector(selectSingleObject);
  const activeObjectTab = useSelector(selectSingleObjectActiveTab);
  const isObjectCardActiveTab =
    (activeObjectTab && activeObjectTab === DocumentationTabTypes.CARDS_LIST) || isExpandedMode;

  const { backToObjectList, backToElementsList } = useObjectListInteractions();
  const { handleCloseUniversalSearch } = useObjectListUniversalSearch({
    crmOrgId: '', // the crmOrgId is not used in this context
  });

  const resetTabState = useCallback(() => {
    handleCloseUniversalSearch();
    dispatch(setActivePageTab({ tabId: '' }));
    dispatch(
      setDocumentationPageType({ documentationPageType: DocumentationPageType.metadataTypes }),
    );
  }, [dispatch, handleCloseUniversalSearch]);

  const breadcrumbList = useMemo(() => {
    const isDocumentationPageTagsAndUsers =
      documentationPageType === DocumentationPageType.tagsAndUsers;
    const isDocumentationPageUniversalSearch =
      documentationPageType === DocumentationPageType.universalSearch;
    const isDocumentationPageMetadataTypes =
      documentationPageType === DocumentationPageType.metadataTypes;

    const baseList: Breadcrumb[] = [
      {
        value: 'Documentation',
        label: 'Documentation',
        onClick: resetTabState,
      },
    ];

    if (isDocumentationPageTagsAndUsers) {
      baseList.push({
        value: 'Custom annotations',
        label: 'Custom annotations',
        onClick: () => {
          dispatch(setActivePageTab({ tabId: '' }));
        },
      });
    }

    if (isDocumentationPageUniversalSearch) {
      baseList.push({
        value: 'Search',
        label: 'Search',
        onClick: () => {},
      });
    }

    if (activePageTab && !isDocumentationPageUniversalSearch) {
      baseList.push({
        value: activePageTab as string,
        label: activePageTab
          ? ((pageTypeToName[activePageTab as SubPageTypes] as string) ??
            getMetadataLabel(activePageTab))
          : '',
        onClick: backToObjectList,
      });
    }

    if (isDocumentationPageMetadataTypes) {
      if (objectTypeName && !baseList.find((b) => b.value === objectTypeName.objectType)) {
        baseList.push({
          value: objectTypeName.objectType,
          label: objectTypeName.label,
          onClick: backToElementsList,
        });
      }

      if (objectTypeName && activeObjectTab && !isObjectCardActiveTab) {
        baseList.push({ label: activeObjectTab, value: activeObjectTab, onClick: () => {} });
      }
    }

    return baseList;
  }, [
    resetTabState,
    activePageTab,
    backToObjectList,
    objectTypeName,
    activeObjectTab,
    isObjectCardActiveTab,
    documentationPageType,
    backToElementsList,
    dispatch,
  ]);

  return (
    <ConfigurationCanvasPanelHeader
      onClose={onCloseDialog}
      withExpandedModeButton={!!withExpandedModeButton}
    >
      {isLightboxOpened && universalSearchRedesign && (
        <DocumentationLightbox onClose={() => setIsLightboxOpened(false)} crmOrgId={crmOrgId} />
      )}

      <Stack direction="row" alignItems="flex-start" gap={0} width="100%">
        {renderBreadcrumbs &&
        (activePageTab || documentationPageType !== DocumentationPageType.metadataTypes) ? (
          <Breadcrumbs breadcrumbList={breadcrumbList} />
        ) : (
          <Typography variant={isExpandedMode ? 'h2' : 'h4-medium'}>Documentation</Typography>
        )}

        {documentationPageType !== DocumentationPageType.universalSearch && (
          <>
            <Box sx={{ flex: 1 }} />
            {isExpandedMode && (
              <Box
                sx={{
                  alignSelf: 'center',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  marginTop: isCanvasUnited ? '5px' : '0px',
                }}
              >
                <Button
                  variant="system"
                  startIconName="Search"
                  size={isCanvasUnited ? 'tiny' : 'small'}
                  onClick={() => {
                    if (universalSearchRedesign) {
                      setIsLightboxOpened(true);
                    } else {
                      dispatch(
                        setDocumentationPageType({
                          documentationPageType: DocumentationPageType.universalSearch,
                        }),
                      );
                    }
                  }}
                >
                  Search all metadata
                </Button>
              </Box>
            )}
            {!isExpandedMode && (
              <IconButton
                size={isCanvasUnited ? 'tiny' : 'small'}
                variant="flat"
                onClick={() => {
                  if (universalSearchRedesign) {
                    setIsLightboxOpened(true);
                  } else {
                    dispatch(
                      setDocumentationPageType({
                        documentationPageType: DocumentationPageType.universalSearch,
                      }),
                    );
                  }
                }}
              >
                <Search />
              </IconButton>
            )}
          </>
        )}
      </Stack>
    </ConfigurationCanvasPanelHeader>
  );
};
